import { useId } from "react";

import { Button } from "../../../../../core/Button.js";
import { Text } from "../../../../../core/Text.js";
import { Amount } from "../../../../../domain/Amount.js";
import { AppLink } from "../../../../../foundations/AppLink.js";
import { cn } from "../../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../../foundations/interaction.js";
import { ActionButton } from "../../../../shared/ActionButton.js";
import type { BattleEntryContent } from "../../../battlesContent.js";

/**
 * What a seat costs and the one way in: the entry, the spendable balance beside it, why it falls
 * short when it does, then Create (the brand action) and Cancel.
 */
export function BattleEntry({
  title,
  summary,
  priceCents,
  balanceNote,
  short,
  submit,
  cancel,
}: BattleEntryContent) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col">
          <Text as="h2" id={id} variant="body-b2" tone="secondary">
            {title}
          </Text>
          <Text variant="body-b3" tone="tertiary">
            {summary}
          </Text>
        </div>
        <Amount cents={priceCents} size="lg" className="text-text-primary" />
      </div>
      {balanceNote && (
        <Text as="p" variant="body-b3" tone="tertiary">
          {balanceNote}
        </Text>
      )}
      {short && (
        <Text as="p" variant="body-b3" tone="danger">
          {short.message}{" "}
          {short.link && (
            <AppLink
              href={short.link.href}
              className={cn("rounded-xs text-accent-text underline", FOCUS_RING)}
            >
              {short.link.label}
            </AppLink>
          )}
        </Text>
      )}
      <Button
        variant="brand"
        size="lg"
        fullWidth
        disabled={submit.disabled}
        loading={submit.loading}
        onClick={submit.onSubmit}
      >
        {submit.label}
      </Button>
      <ActionButton
        action={{ label: cancel.label, href: cancel.href }}
        variant="ghost"
        size="sm"
        className="self-center"
      />
    </section>
  );
}
